import { computed, inject, Injectable, isSignal, ResourceRef, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { select } from '@ngxs/store';
import { SonaAuthFacade, SonaUsageFacade } from '@sona/angular-auth-domain';
import { Feature, MaybeConditionalFeature, Organization } from '@sona/shared-model-common';
import { checkPermission } from '@sona/shared-util-common';
import { filter, map } from 'rxjs';


@Injectable({
    providedIn: 'root'
})
export class SonaAuthApiFacade {

    /**
     * The authenticated user
     */
    static readonly user = SonaAuthFacade.user;

    private readonly _authFacade = inject(SonaAuthFacade);
    private readonly _usageFacade = inject(SonaUsageFacade);

    readonly user = this._authFacade.user;
    readonly organizationId = this._authFacade.organizationId;
    readonly organizationName = this._authFacade.organizationName;
    readonly featurebaseIdentityVerificationHash = this._authFacade.featurebaseIdentityVerificationHash;
    readonly organizationFeaturebaseIdentityVerificationHash = this._authFacade.organizationFeaturebaseIdentityVerificationHash;
    readonly organization: ResourceRef<Organization> = this._authFacade.organization;
    readonly switchOrganization = this._authFacade.switchOrganization;

    readonly isAuthenticated = select(SonaAuthFacade.isAuthenticated);
    readonly usage = this._usageFacade.usage;
    readonly limits = this._usageFacade.limits;
    readonly limits$ = toObservable(this._usageFacade.limitsResource.value);
    readonly subscription = computed(() => this._usageFacade.usage()?.subscription);
    protected readonly _features = computed(() => this.limits()?.features);

    hasPermission<F extends Feature>(feature: MaybeConditionalFeature<F> | Signal<MaybeConditionalFeature<F>>) {
        return computed(() => {
            const availableFeatures = this._features();

            if (!availableFeatures) {
                return false;
            }

            return checkPermission(availableFeatures, isSignal(feature) ? feature() : feature);
        });
    }

    hasAllPermissions(features: MaybeConditionalFeature[] | Signal<MaybeConditionalFeature[]>) {
        return computed(() => {
            const availableFeatures = this._features();

            if (!availableFeatures) {
                return false;
            }

            const featureList = isSignal(features) ? features() : features;

            return featureList.every((feature) => checkPermission(availableFeatures, feature));
        });
    }

    hasPermissionAsync(feature: Feature) {
        return this.limits$.pipe(
            filter((limits) => limits != null),
            map((limits) => limits.features?.[feature] ?? false)
        );
    }

    getFeature(feature: Feature) {
        return computed(() => this._features()?.[feature]);
    }
}
